export type HashableFile = {
    size: number;
    slice: (start: number, end: number) => Blob;
};

const HASH_SAMPLE_SIZE = 1024 * 1024; // 1 MB

async function blobToArrayBuffer(blob: Blob): Promise<ArrayBuffer> {
    if (typeof blob.arrayBuffer === 'function') {
        return blob.arrayBuffer();
    }

    return new Promise<ArrayBuffer>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as ArrayBuffer);
        reader.onerror = () => reject(reader.error);
        reader.readAsArrayBuffer(blob);
    });
}

/**
 * Quick duplicate-detection fingerprint: SHA256( first 1MB + last 1MB + utf8(size) ), hex lowercase.
 * We sample head+tail instead of hashing the whole file so multi-GB videos stay cheap.
 * This is advisory only (used to warn on duplicate uploads), not a cryptographic identity guarantee.
 */
export async function computeFileHash(file: HashableFile): Promise<string> {
    const { size } = file;
    const head = new Uint8Array(await blobToArrayBuffer(file.slice(0, Math.min(HASH_SAMPLE_SIZE, size))));
    const tail = new Uint8Array(await blobToArrayBuffer(file.slice(Math.max(0, size - HASH_SAMPLE_SIZE), size)));
    const sizeBytes = new TextEncoder().encode(size.toString());

    const buffer = new Uint8Array(head.length + tail.length + sizeBytes.length);
    buffer.set(head, 0);
    buffer.set(tail, head.length);
    buffer.set(sizeBytes, head.length + tail.length);

    const digest = await crypto.subtle.digest('SHA-256', buffer);

    return Array.from(new Uint8Array(digest))
        .map((byte) => byte.toString(16).padStart(2, '0'))
        .join('');
}
